import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageCircle, Music2, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import AssistantThread from "@/components/creator/assistant/AssistantThread";
import AssistantComposer from "@/components/creator/assistant/AssistantComposer";
import PastChats from "@/components/creator/assistant/PastChats";
import useAssistantChat from "@/components/creator/assistant/useAssistantChat";
import { safeApiError } from "@/lib/safeErrorText";

/** Which song is open on this page: a project, or a library song (song / record / master / stems pages). */
export function openSongFromLocation(pathname, search) {
  const p = pathname.match(/^\/create\/project\/([^/]+)/);
  if (p) return { projectId: p[1], songId: null };
  const s = pathname.match(/^\/create\/(?:song|record|master)\/([^/]+)/);
  if (s) return { projectId: null, songId: s[1] };
  return { projectId: new URLSearchParams(search).get("project"), songId: null };
}

/** Header button + side panel: a ChatGPT studio assistant that reads the open song. */
export default function StudioAssistant() {
  const location = useLocation();
  const { projectId, songId } = openSongFromLocation(location.pathname, location.search);
  const chat = useAssistantChat({ projectId, songId });
  const [open, setOpen] = useState(false);
  useEffect(() => {
    document.body.dataset.assistantOpen = open ? "1" : "0";
    return () => { document.body.dataset.assistantOpen = "0"; };
  }, [open]);
  const hasSong = Boolean(projectId || songId);
  const openChat = (id) => chat.open(id).catch((e) => toast.error("Couldn't open that chat", { description: safeApiError(e) }));
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button type="button" data-testid="assistant-open-btn" aria-label="Ask ChatGPT"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-amber-300/40 bg-amber-500/10 px-3 py-2 font-display text-[13px] font-medium text-amber-100 transition-colors hover:bg-amber-500/20">
          <MessageCircle className="h-4 w-4" /> <span className="hidden sm:inline">Ask ChatGPT</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="assistant-panel" className="flex w-full flex-col gap-4 border-white/10 bg-[#05070f]/95 p-5 text-white backdrop-blur-2xl sm:max-w-[460px]">
        <div className="pr-8">
          <SheetTitle className="font-display text-lg font-semibold text-white">Studio assistant</SheetTitle>
          <SheetDescription className="mt-1 text-[12px] text-white/55">ChatGPT · GPT-6 Sol — your producer, arranger and vocal coach.</SheetDescription>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span data-testid="assistant-context" className={`inline-flex max-w-full items-center gap-1.5 truncate rounded-full px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${hasSong ? "bg-emerald-500/15 text-emerald-200 ring-1 ring-emerald-400/30" : "bg-white/[0.06] text-white/50"}`}>
              <Music2 className="h-3 w-3 shrink-0" /> {hasSong ? (chat.songTitle ? `Reading: ${chat.songTitle}` : "Reads the open song") : "No song open"}
            </span>
            <div className="ml-auto flex shrink-0 items-center gap-2">
              <PastChats currentId={chat.sessionId} onOpen={openChat} onDeleted={chat.reset} />
              <button type="button" data-testid="assistant-new-chat" onClick={chat.reset} disabled={chat.streaming}
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-wider text-white/70 hover:bg-white/[0.08] hover:text-white disabled:opacity-40">
                <Plus className="h-3.5 w-3.5" /> New
              </button>
            </div>
          </div>
        </div>
        <AssistantThread messages={chat.messages} hasSong={hasSong} onPick={chat.send} disabled={chat.streaming} />
        {chat.error ? <p data-testid="assistant-error" className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-[12px] text-rose-200">{chat.error}</p> : null}
        <AssistantComposer onSend={chat.send} busy={chat.streaming} />
      </SheetContent>
    </Sheet>
  );
}
